/**
 * Appointment Type Configuration
 */

export interface AppointmentTypeConfig {
  id: string
  name: string // Display name (e.g., "Consultation", "Follow-up")
  code: string // Unique identifier (e.g., "consultation", "follow-up")
  isDefault: boolean // Whether this is a default system type (cannot be deleted)
  isActive: boolean // Whether this type is currently active/available
  colors: {
    background: string
    border: string
    hoverBackground: string
    hoverBorder: string
  }
  createdAt: string
  updatedAt: string
  createdBy?: string
  updatedBy?: string
}

export interface AppointmentTypeFormData {
  name: string
  code: string
  colors: {
    background: string
    border: string
    hoverBackground: string
    hoverBorder: string
  }
}

// Default appointment types with their default colors
export const DEFAULT_APPOINTMENT_TYPES: Omit<AppointmentTypeConfig, 'id' | 'createdAt' | 'updatedAt'>[] = [
  {
    name: 'Consultation',
    code: 'consultation',
    isDefault: true,
    isActive: true,
    colors: {
      background: '#eff6ff',
      border: '#bfdbfe',
      hoverBackground: '#dbeafe',
      hoverBorder: '#93c5fd',
    },
  },
  {
    name: 'Follow-up',
    code: 'follow-up',
    isDefault: true,
    isActive: true,
    colors: {
      background: '#f0fdf4',
      border: '#bbf7d0',
      hoverBackground: '#dcfce7',
      hoverBorder: '#86efac',
    },
  },
  {
    name: 'Checkup',
    code: 'checkup',
    isDefault: true,
    isActive: true,
    colors: {
      background: '#fef3c7',
      border: '#fde68a',
      hoverBackground: '#fde68a',
      hoverBorder: '#fcd34d',
    },
  },
  {
    name: 'Procedure',
    code: 'procedure',
    isDefault: true,
    isActive: true,
    colors: {
      background: '#fce7f3',
      border: '#fbcfe8',
      hoverBackground: '#f9a8d4',
      hoverBorder: '#f472b6',
    },
  },
  {
    name: 'Emergency',
    code: 'emergency',
    isDefault: true,
    isActive: true,
    colors: {
      background: '#fee2e2',
      border: '#fecaca',
      hoverBackground: '#fecaca',
      hoverBorder: '#f87171',
    },
  },
  {
    name: 'Other',
    code: 'other',
    isDefault: true,
    isActive: true,
    colors: {
      background: '#f3f4f6',
      border: '#d1d5db',
      hoverBackground: '#e5e7eb',
      hoverBorder: '#9ca3af',
    },
  },
]

// Color palette grouping for UI organization
export interface ColorPaletteGroup {
  name: string
  palettes: ColorPalette[]
}

export interface ColorPalette {
  name: string
  background: string
  border: string
  hoverBackground: string
  hoverBorder: string
}

// Predefined color palettes for auto-selection
export const COLOR_PALETTES: ColorPalette[] = [
  // Blues
  { name: 'Blue', background: '#eff6ff', border: '#bfdbfe', hoverBackground: '#dbeafe', hoverBorder: '#93c5fd' },
  { name: 'Sky Blue', background: '#e0f2fe', border: '#bae6fd', hoverBackground: '#bae6fd', hoverBorder: '#7dd3fc' },
  { name: 'Navy Blue', background: '#dbeafe', border: '#93c5fd', hoverBackground: '#93c5fd', hoverBorder: '#60a5fa' },
  { name: 'Light Blue', background: '#f0f9ff', border: '#bae6fd', hoverBackground: '#e0f2fe', hoverBorder: '#7dd3fc' },
  { name: 'Cyan', background: '#ecfeff', border: '#a5f3fc', hoverBackground: '#cffafe', hoverBorder: '#67e8f9' },
  
  // Greens
  { name: 'Green', background: '#f0fdf4', border: '#bbf7d0', hoverBackground: '#dcfce7', hoverBorder: '#86efac' },
  { name: 'Emerald', background: '#ecfdf5', border: '#a7f3d0', hoverBackground: '#d1fae5', hoverBorder: '#6ee7b7' },
  { name: 'Lime', background: '#f7fee7', border: '#d9f99d', hoverBackground: '#ecfccb', hoverBorder: '#bef264' },
  { name: 'Mint', background: '#f0fdfa', border: '#b2f5ea', hoverBackground: '#ccfbf1', hoverBorder: '#80f0d6' },
  { name: 'Forest Green', background: '#f0fdf4', border: '#86efac', hoverBackground: '#dcfce7', hoverBorder: '#4ade80' },
  { name: 'Olive', background: '#fefce8', border: '#fef08a', hoverBackground: '#fef9c3', hoverBorder: '#fde047' },
  
  // Yellows & Oranges
  { name: 'Yellow', background: '#fef3c7', border: '#fde68a', hoverBackground: '#fde68a', hoverBorder: '#fcd34d' },
  { name: 'Amber', background: '#fffbeb', border: '#fde68a', hoverBackground: '#fef3c7', hoverBorder: '#fbbf24' },
  { name: 'Gold', background: '#fffbeb', border: '#fcd34d', hoverBackground: '#fef3c7', hoverBorder: '#fbbf24' },
  { name: 'Orange', background: '#fff7ed', border: '#fed7aa', hoverBackground: '#fed7aa', hoverBorder: '#fdba74' },
  { name: 'Tangerine', background: '#fff4e6', border: '#ffd9b3', hoverBackground: '#ffe6cc', hoverBorder: '#ffb366' },
  { name: 'Peach', background: '#fff1f2', border: '#fecdd3', hoverBackground: '#ffe4e6', hoverBorder: '#fda4af' },
  
  // Reds & Pinks
  { name: 'Red', background: '#fee2e2', border: '#fecaca', hoverBackground: '#fecaca', hoverBorder: '#f87171' },
  { name: 'Rose', background: '#fff1f2', border: '#fecdd3', hoverBackground: '#ffe4e6', hoverBorder: '#fda4af' },
  { name: 'Pink', background: '#fce7f3', border: '#fbcfe8', hoverBackground: '#f9a8d4', hoverBorder: '#f472b6' },
  { name: 'Coral', background: '#fff1f2', border: '#fda4af', hoverBackground: '#fecdd3', hoverBorder: '#fb7185' },
  { name: 'Salmon', background: '#fff5f5', border: '#fecaca', hoverBackground: '#fee2e2', hoverBorder: '#fca5a5' },
  { name: 'Cherry', background: '#fee2e2', border: '#f87171', hoverBackground: '#fecaca', hoverBorder: '#ef4444' },
  
  // Purples & Violets
  { name: 'Purple', background: '#f3e8ff', border: '#e9d5ff', hoverBackground: '#e9d5ff', hoverBorder: '#d8b4fe' },
  { name: 'Violet', background: '#f5f3ff', border: '#ddd6fe', hoverBackground: '#e9d5ff', hoverBorder: '#c4b5fd' },
  { name: 'Lavender', background: '#faf5ff', border: '#e9d5ff', hoverBackground: '#f3e8ff', hoverBorder: '#d8b4fe' },
  { name: 'Plum', background: '#faf5ff', border: '#d8b4fe', hoverBackground: '#e9d5ff', hoverBorder: '#c084fc' },
  { name: 'Indigo', background: '#eef2ff', border: '#c7d2fe', hoverBackground: '#c7d2fe', hoverBorder: '#a5b4fc' },
  { name: 'Deep Purple', background: '#ede9fe', border: '#c4b5fd', hoverBackground: '#ddd6fe', hoverBorder: '#a78bfa' },
  
  // Teals & Cyans
  { name: 'Teal', background: '#f0fdfa', border: '#b2f5ea', hoverBackground: '#b2f5ea', hoverBorder: '#5eead4' },
  { name: 'Turquoise', background: '#ecfeff', border: '#a5f3fc', hoverBackground: '#cffafe', hoverBorder: '#67e8f9' },
  { name: 'Aqua', background: '#e0f2fe', border: '#7dd3fc', hoverBackground: '#bae6fd', hoverBorder: '#38bdf8' },
  { name: 'Seafoam', background: '#f0fdfa', border: '#5eead4', hoverBackground: '#b2f5ea', hoverBorder: '#2dd4bf' },
  
  // Grays & Neutrals
  { name: 'Gray', background: '#f3f4f6', border: '#d1d5db', hoverBackground: '#e5e7eb', hoverBorder: '#9ca3af' },
  { name: 'Slate', background: '#f8fafc', border: '#cbd5e1', hoverBackground: '#e2e8f0', hoverBorder: '#94a3b8' },
  { name: 'Stone', background: '#fafaf9', border: '#e7e5e4', hoverBackground: '#f5f5f4', hoverBorder: '#d6d3d1' },
  { name: 'Zinc', background: '#fafafa', border: '#e4e4e7', hoverBackground: '#f4f4f5', hoverBorder: '#d4d4d8' },
  { name: 'Neutral', background: '#fafafa', border: '#e5e5e5', hoverBackground: '#f5f5f5', hoverBorder: '#d4d4d4' },
  { name: 'Warm Gray', background: '#fafaf9', border: '#e7e5e4', hoverBackground: '#f5f5f4', hoverBorder: '#d6d3d1' },
  
  // Special Colors
  { name: 'Brown', background: '#fefdfb', border: '#f5e6d3', hoverBackground: '#f5e6d3', hoverBorder: '#e6c99f' },
  { name: 'Copper', background: '#fff7ed', border: '#fed7aa', hoverBackground: '#fed7aa', hoverBorder: '#fdba74' },
  { name: 'Bronze', background: '#fff7ed', border: '#fcd34d', hoverBackground: '#fef3c7', hoverBorder: '#fbbf24' },
  { name: 'Magenta', background: '#fdf4ff', border: '#f5d0fe', hoverBackground: '#f0abfc', hoverBorder: '#e879f9' },
  { name: 'Fuchsia', background: '#fdf4ff', border: '#f0abfc', hoverBackground: '#f0abfc', hoverBorder: '#e879f9' },
  
  // Pastels
  { name: 'Pastel Blue', background: '#f0f9ff', border: '#bae6fd', hoverBackground: '#e0f2fe', hoverBorder: '#7dd3fc' },
  { name: 'Pastel Green', background: '#f0fdf4', border: '#bbf7d0', hoverBackground: '#dcfce7', hoverBorder: '#86efac' },
  { name: 'Pastel Pink', background: '#fdf2f8', border: '#fbcfe8', hoverBackground: '#f9a8d4', hoverBorder: '#f472b6' },
  { name: 'Pastel Purple', background: '#faf5ff', border: '#e9d5ff', hoverBackground: '#f3e8ff', hoverBorder: '#d8b4fe' },
  { name: 'Pastel Yellow', background: '#fefce8', border: '#fef08a', hoverBackground: '#fef9c3', hoverBorder: '#fde047' },
  { name: 'Pastel Orange', background: '#fff7ed', border: '#fed7aa', hoverBackground: '#fed7aa', hoverBorder: '#fdba74' },
  
  // Vibrant Colors
  { name: 'Electric Blue', background: '#dbeafe', border: '#60a5fa', hoverBackground: '#93c5fd', hoverBorder: '#3b82f6' },
  { name: 'Vibrant Green', background: '#dcfce7', border: '#4ade80', hoverBackground: '#86efac', hoverBorder: '#22c55e' },
  { name: 'Vibrant Purple', background: '#e9d5ff', border: '#a78bfa', hoverBackground: '#c4b5fd', hoverBorder: '#8b5cf6' },
  { name: 'Vibrant Pink', background: '#f9a8d4', border: '#f472b6', hoverBackground: '#fb7185', hoverBorder: '#ec4899' },
  { name: 'Vibrant Orange', background: '#fed7aa', border: '#fb923c', hoverBackground: '#fdba74', hoverBorder: '#f97316' },
  
  // Earth Tones
  { name: 'Sage', background: '#f6f7f6', border: '#d1d5db', hoverBackground: '#e5e7eb', hoverBorder: '#9ca3af' },
  { name: 'Terracotta', background: '#fff7ed', border: '#fdba74', hoverBackground: '#fed7aa', hoverBorder: '#fb923c' },
  { name: 'Sand', background: '#fefce8', border: '#fde047', hoverBackground: '#fef9c3', hoverBorder: '#facc15' },
  { name: 'Khaki', background: '#fefce8', border: '#e7e5e4', hoverBackground: '#f5f5f4', hoverBorder: '#d6d3d1' },
  
  // Cool Tones
  { name: 'Ice Blue', background: '#f0f9ff', border: '#bae6fd', hoverBackground: '#e0f2fe', hoverBorder: '#7dd3fc' },
  { name: 'Frost', background: '#f8fafc', border: '#cbd5e1', hoverBackground: '#e2e8f0', hoverBorder: '#94a3b8' },
  { name: 'Mint Frost', background: '#f0fdfa', border: '#b2f5ea', hoverBackground: '#ccfbf1', hoverBorder: '#80f0d6' },
  
  // Color Hunt Popular Palettes
  { name: 'Ocean Breeze', background: '#E8F4F8', border: '#A8D5E2', hoverBackground: '#C8E4F0', hoverBorder: '#7BC4D8' },
  { name: 'Sunset Glow', background: '#FFF5E4', border: '#FFC4C4', hoverBackground: '#FFE4E1', hoverBorder: '#FF9494' },
  { name: 'Lavender Dream', background: '#F5F0FF', border: '#D4C5F9', hoverBackground: '#E8DDFF', hoverBorder: '#B8A3F0' },
  { name: 'Forest Mist', background: '#F0F7F4', border: '#B8D4C8', hoverBackground: '#D8E8E0', hoverBorder: '#8FC4A8' },
  { name: 'Coral Reef', background: '#FFF0F5', border: '#FFB6C1', hoverBackground: '#FFE0E6', hoverBorder: '#FF8FA3' },
  { name: 'Mint Fresh', background: '#F0FFF4', border: '#90EE90', hoverBackground: '#C8F0D0', hoverBorder: '#7FCD85' },
  { name: 'Peach Blossom', background: '#FFF8F0', border: '#FFD4A3', hoverBackground: '#FFE8C8', hoverBorder: '#FFB84D' },
  { name: 'Sky Gradient', background: '#E6F3FF', border: '#87CEEB', hoverBackground: '#C8E3FF', hoverBorder: '#5FA8E0' },
  { name: 'Rose Gold', background: '#FFF0F5', border: '#E8B4B8', hoverBackground: '#F5D7DC', hoverBorder: '#D89BA0' },
  { name: 'Emerald Shine', background: '#E8F8F5', border: '#7FB3A3', hoverBackground: '#C8E8DC', hoverBorder: '#5FA08A' },
  { name: 'Buttercup', background: '#FFFEF0', border: '#FFE87C', hoverBackground: '#FFF9C4', hoverBorder: '#FFD93D' },
  { name: 'Lilac', background: '#F5F0FF', border: '#C8A2C8', hoverBackground: '#E0D0E0', hoverBorder: '#B08FB0' },
  { name: 'Turquoise Wave', background: '#E0F7FA', border: '#4DD0E1', hoverBackground: '#B2EBF2', hoverBorder: '#26C6DA' },
  { name: 'Apricot', background: '#FFF8F0', border: '#FFB84D', hoverBackground: '#FFE0B2', hoverBorder: '#FF9800' },
  { name: 'Periwinkle', background: '#E8E8FF', border: '#B8B8FF', hoverBackground: '#D0D0FF', hoverBorder: '#9C9CFF' },
  { name: 'Sage Green', background: '#F0F4F0', border: '#9CAF88', hoverBackground: '#D8E0D8', hoverBorder: '#7A8F6A' },
  { name: 'Dusty Rose', background: '#FFF0F5', border: '#D4A5A5', hoverBackground: '#F0D0D0', hoverBorder: '#B88585' },
  { name: 'Azure', background: '#E6F2FF', border: '#66B2FF', hoverBackground: '#B3D9FF', hoverBorder: '#3399FF' },
  { name: 'Honey', background: '#FFF8E1', border: '#FFD54F', hoverBackground: '#FFECB3', hoverBorder: '#FFC107' },
  { name: 'Lavender Mist', background: '#F3E5F5', border: '#CE93D8', hoverBackground: '#E1BEE7', hoverBorder: '#BA68C8' },
  { name: 'Blush', background: '#FFF0F5', border: '#F8BBD0', hoverBackground: '#FCE4EC', hoverBorder: '#F48FB1' },
  { name: 'Powder Blue', background: '#E3F2FD', border: '#90CAF9', hoverBackground: '#BBDEFB', hoverBorder: '#64B5F6' },
  { name: 'Peach Cream', background: '#FFF5E6', border: '#FFCC80', hoverBackground: '#FFE0B2', hoverBorder: '#FFB74D' },
  { name: 'Mauve', background: '#F3E5F5', border: '#BA68C8', hoverBackground: '#E1BEE7', hoverBorder: '#AB47BC' },
  { name: 'Aquamarine', background: '#E0F7FA', border: '#4DD0E1', hoverBackground: '#B2EBF2', hoverBorder: '#00BCD4' },
  { name: 'Cream', background: '#FFFEF5', border: '#F5E6D3', hoverBackground: '#FFF8E1', hoverBorder: '#E6D5B8' },
  { name: 'Soft Pink', background: '#FFF0F5', border: '#F8BBD0', hoverBackground: '#FCE4EC', hoverBorder: '#F48FB1' },
  { name: 'Sky Mist', background: '#E8F4F8', border: '#81D4FA', hoverBackground: '#B3E5FC', hoverBorder: '#4FC3F7' },
  { name: 'Lemon Chiffon', background: '#FFFEF0', border: '#FFF59D', hoverBackground: '#FFF9C4', hoverBorder: '#FFEB3B' },
]

// Group palettes by category for better UI organization
export const COLOR_PALETTE_GROUPS: ColorPaletteGroup[] = [
  {
    name: 'Blues',
    palettes: COLOR_PALETTES.filter((p) =>
      ['Blue', 'Sky Blue', 'Navy Blue', 'Light Blue', 'Cyan', 'Ice Blue', 'Azure', 'Powder Blue', 'Sky Mist', 'Sky Gradient', 'Turquoise Wave', 'Aquamarine'].includes(p.name)
    ),
  },
  {
    name: 'Greens',
    palettes: COLOR_PALETTES.filter((p) =>
      ['Green', 'Emerald', 'Lime', 'Mint', 'Forest Green', 'Olive', 'Mint Fresh', 'Emerald Shine', 'Forest Mist', 'Sage Green', 'Seafoam'].includes(p.name)
    ),
  },
  {
    name: 'Yellows & Oranges',
    palettes: COLOR_PALETTES.filter((p) =>
      ['Yellow', 'Amber', 'Gold', 'Orange', 'Tangerine', 'Peach', 'Buttercup', 'Honey', 'Lemon Chiffon', 'Peach Blossom', 'Apricot', 'Peach Cream'].includes(p.name)
    ),
  },
  {
    name: 'Reds & Pinks',
    palettes: COLOR_PALETTES.filter((p) =>
      ['Red', 'Rose', 'Pink', 'Coral', 'Salmon', 'Cherry', 'Coral Reef', 'Rose Gold', 'Blush', 'Soft Pink', 'Dusty Rose', 'Sunset Glow'].includes(p.name)
    ),
  },
  {
    name: 'Purples & Violets',
    palettes: COLOR_PALETTES.filter((p) =>
      ['Purple', 'Violet', 'Lavender', 'Plum', 'Indigo', 'Deep Purple', 'Lavender Dream', 'Lilac', 'Periwinkle', 'Lavender Mist', 'Mauve'].includes(p.name)
    ),
  },
  {
    name: 'Teals & Cyans',
    palettes: COLOR_PALETTES.filter((p) =>
      ['Teal', 'Turquoise', 'Aqua', 'Seafoam', 'Ocean Breeze', 'Turquoise Wave', 'Aquamarine'].includes(p.name)
    ),
  },
  {
    name: 'Grays & Neutrals',
    palettes: COLOR_PALETTES.filter((p) =>
      ['Gray', 'Slate', 'Stone', 'Zinc', 'Neutral', 'Warm Gray', 'Frost', 'Cream'].includes(p.name)
    ),
  },
  {
    name: 'Pastels',
    palettes: COLOR_PALETTES.filter((p) =>
      ['Pastel Blue', 'Pastel Green', 'Pastel Pink', 'Pastel Purple', 'Pastel Yellow', 'Pastel Orange'].includes(p.name)
    ),
  },
  {
    name: 'Vibrant',
    palettes: COLOR_PALETTES.filter((p) =>
      ['Electric Blue', 'Vibrant Green', 'Vibrant Purple', 'Vibrant Pink', 'Vibrant Orange'].includes(p.name)
    ),
  },
  {
    name: 'Special & Earth Tones',
    palettes: COLOR_PALETTES.filter((p) =>
      ['Brown', 'Copper', 'Bronze', 'Magenta', 'Fuchsia', 'Sage', 'Terracotta', 'Sand', 'Khaki'].includes(p.name)
    ),
  },
]
